import { useMemo, useState } from "react";
import type { ColumnFiltersState, PaginationState } from "@tanstack/react-table";
import { Plus } from "lucide-react";

import { pageCount } from "@/core/api/pagination";
import { FLASHPAY_ROLES, RequireRole } from "@/core/auth";
import { DataTableClar } from "@/core/components/shared/data-table";
import { Button } from "@/core/components/ui/button";
import { useFeeRules } from "@/features/fee-engine/api/use-fee-rules";
import type { FeeRuleFilter } from "@/features/fee-engine/api/dto";
import {
  FeeRuleFormDialog,
  feeRulesColumns,
  type FeeRuleRow,
} from "@/features/fee-engine/components";
import { useFeeEngineTranslation } from "@/features/fee-engine/i18n/use-fee-engine-translation";

/** Traduit les filtres de colonnes DataTableClar en `FeeRuleFilter` (§4.2). */
const toFeeRuleFilter = (filters: ColumnFiltersState): FeeRuleFilter => {
  const filter: FeeRuleFilter = {};
  for (const { id, value } of filters) {
    if (typeof value !== "string" || !value) continue;
    if (id === "productCode") filter.productCode = value;
    if (id === "customerSegment")
      filter.customerSegment = value as FeeRuleFilter["customerSegment"];
    if (id === "feePurpose")
      filter.feePurpose = value as FeeRuleFilter["feePurpose"];
    if (id === "status") filter.status = value as FeeRuleFilter["status"];
  }
  return filter;
};

/**
 * Liste paginée des règles de frais — configuration tarifaire back-office
 * (`POST /FeeRules/all`, guide §6.5). Écriture réservée aux rôles
 * flashpay-superadmin / flashpay-admin.
 */
export function FeeRulesPage() {
  const { t } = useFeeEngineTranslation();
  const [createOpen, setCreateOpen] = useState(false);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 20,
  });
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);

  const params = useMemo(
    () => ({
      filter: toFeeRuleFilter(columnFilters),
      page: pagination.pageIndex + 1,
      pageSize: pagination.pageSize,
    }),
    [columnFilters, pagination]
  );

  const { data, isLoading, isFetching, error, refetch } = useFeeRules(params);

  return (
    <section className="flex flex-col gap-4">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-semibold text-foreground">
          {t("fees.rules.title")}
        </h1>
        <RequireRole
          roles={[FLASHPAY_ROLES.SUPERADMIN, FLASHPAY_ROLES.ADMIN]}
        >
          <Button onClick={() => setCreateOpen(true)}>
            <Plus className="mr-2 w-4 h-4" />
            {t("fees.rules.create")}
          </Button>
        </RequireRole>
      </div>

      <DataTableClar
        data={(data?.items ?? []).filter((r): r is FeeRuleRow => Boolean(r.id))}
        columnsClar={feeRulesColumns}
        title={t("fees.rules.title")}
        exportFileName="fee-rules"
        paginationMode="server"
        serverPageCount={data ? pageCount(data) : 0}
        totalItemsCount={data?.total ?? 0}
        pagination={pagination}
        onPaginationChange={setPagination}
        columnFilters={columnFilters}
        onColumnFiltersChange={setColumnFilters}
        loading={{ isLoading, isFetching }}
        error={
          error
            ? { message: error.message, refetch: () => void refetch() }
            : undefined
        }
      />

      <FeeRuleFormDialog open={createOpen} onOpenChange={setCreateOpen} />
    </section>
  );
}
